import { type ReactNode } from 'react'
import { Icon } from '@iconify/react'
import { cn } from '@/lib/utils'

type BadgeVariant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
type BadgeSize = 'sm' | 'md' | 'lg'

interface BadgeProps {
  variant?: BadgeVariant
  size?: BadgeSize
  icon?: string
  dot?: boolean
  children: ReactNode
  className?: string
}

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-slate-100 text-slate-700',
  primary: 'bg-blue-100 text-blue-700',
  secondary: 'bg-slate-200 text-slate-600',
  success: 'bg-emerald-100 text-emerald-700',
  warning: 'bg-amber-100 text-amber-700',
  danger: 'bg-rose-100 text-rose-700',
  info: 'bg-sky-100 text-sky-700',
}

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'px-1.5 py-0.5 text-xs',
  md: 'px-2 py-0.5 text-xs',
  lg: 'px-2.5 py-1 text-sm',
}

const iconSizes: Record<BadgeSize, number> = {
  sm: 12,
  md: 14,
  lg: 16,
}

const dotSizes: Record<BadgeSize, string> = {
  sm: 'w-1.5 h-1.5',
  md: 'w-2 h-2',
  lg: 'w-2.5 h-2.5',
}

export function Badge({
  variant = 'default',
  size = 'md',
  icon,
  dot,
  children,
  className,
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-medium rounded-full whitespace-nowrap',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
    >
      {dot && (
        <span
          className={cn(
            'rounded-full',
            dotSizes[size],
            variant === 'default' && 'bg-slate-500',
            variant === 'primary' && 'bg-blue-500',
            variant === 'secondary' && 'bg-slate-400',
            variant === 'success' && 'bg-emerald-500',
            variant === 'warning' && 'bg-amber-500',
            variant === 'danger' && 'bg-rose-500',
            variant === 'info' && 'bg-sky-500'
          )}
        />
      )}
      {icon && <Icon icon={icon} width={iconSizes[size]} />}
      {children}
    </span>
  )
}

// Status-specific badge
interface StatusBadgeProps {
  status: string
  label: string
  size?: BadgeSize
  className?: string
}

const statusVariants: Record<string, BadgeVariant> = {
  // Project statuses
  'tor-prep': 'default',
  'purchase-request': 'warning',
  'invitation-issued': 'primary',
  'award-approved': 'success',
  'contract-management': 'success',
  'project-cancelled': 'danger',
  // OCR statuses
  queued: 'default',
  processing: 'warning',
  done: 'success',
  error: 'danger',
  // RFQ statuses
  draft: 'default',
  model_filled: 'warning',
  human_reviewed: 'info',
  published: 'success',
  archived: 'default',
  // Crawl statuses
  running: 'warning',
  success: 'success',
}

export function StatusBadge({ status, label, size = 'md', className }: StatusBadgeProps) {
  const variant = statusVariants[status] || 'default'

  return (
    <Badge variant={variant} size={size} dot className={className}>
      {label}
    </Badge>
  )
}

// Count badge (for notifications, etc.)
interface CountBadgeProps {
  count: number
  max?: number
  variant?: BadgeVariant
  className?: string
}

export function CountBadge({
  count,
  max = 99,
  variant = 'primary',
  className,
}: CountBadgeProps) {
  const displayCount = count > max ? `${max}+` : count.toString()

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-semibold rounded-full',
        variantStyles[variant],
        className
      )}
    >
      {displayCount}
    </span>
  )
}
